<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="payment-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalDescription }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.modal.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
        <aside class="app-modal__menu" aria-label="Secciones del formulario de plan de pagos">
          <button
            v-for="section in visibleSections"
            :key="section.key"
            class="app-modal__menu-item"
            :class="{ active: activeSectionKey === section.key }"
            type="button"
            @click="activeSectionKey = section.key"
          >
            <span class="app-modal__menu-icon">
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
            </span>
            <span class="app-modal__menu-copy">
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
          </button>
        </aside>

        <div class="app-modal__content">
          <form class="app-modal__form" @submit.prevent="submitForm">
            <PaymentPlanModalSectionGeneral
              :active="activeSectionKey === 'general'"
              :form="form"
              :options="options"
              :show-customer-selector="showCustomerSelector"
              :customer-options="normalizedCustomerOptions"
              :edit-permissions="resolvedEditPermissions"
              :sale-price="salePrice"
              :existing-planned-total="adjustedExistingPlannedTotal"
              :remaining-balance="remainingBalanceBeforeCurrent"
              :max-amount-per-installment="maxAmountPerInstallment"
              :is-edit-mode="isEditMode"
              :is-hydrating="isHydratingInitialData"
              :tax-preview="taxPreview"
              :tax-preview-loading="taxPreviewLoading"
              :first-installment-options="firstInstallmentOptions"
              :v$="v$"
            />

            <!--
              Facturacion temporalmente oculta.
              Se reactivara en la siguiente release.
            -->
            <!-- <PaymentPlanModalSectionBilling
              :active="activeSectionKey === 'billing'"
              :form="form"
              :options="options"
              :disabled="!resolvedEditPermissions.can_edit_billing"
            /> -->

            <PaymentPlanModalSectionDetails
              :active="activeSectionKey === 'details'"
              :form="form"
              :options="options"
              :disabled="!resolvedEditPermissions.can_edit_details"
            />

            <footer class="app-modal__footer">
              <div class="app-modal__footer-right">
                <button type="button" class="btn btn-light" @click="emitClose">{{ text.modal.buttons.cancel }}</button>
                <button type="submit" class="btn btn-primary">
                  {{ submitting ? text.modal.buttons.submitting : submitButtonLabel }}
                </button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </section>

    <ConfirmActionModal
      :show="isOverrunConfirmOpen"
      :title="text.overrunConfirm.title"
      :subtitle="text.overrunConfirm.subtitle"
      :message="overrunConfirmMessage"
      :confirm-text="text.overrunConfirm.confirm"
      :cancel-text="text.overrunConfirm.cancel"
      @confirm="confirmOverrunSubmit"
      @cancel="cancelOverrunSubmit"
    />
  </div>
</template>

<script>
import taxesService from '@/services/taxes';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import {
  PAYMENT_PLAN_MODAL_SECTIONS,
  buildPaymentPlanPayload,
  buildPaymentPlanTaxPreviewPayload,
  buildFirstInstallmentOptions,
  createInitialPaymentPlanForm,
  createInitialPaymentPlanModalOptions,
  createInitialPaymentPlanTaxPreview,
} from '@/composables/paymentPlans/usePaymentPlanModalForm';
import { fetchPaymentPlanModalCatalogs } from '@/composables/paymentPlans/usePaymentPlanModalCatalogs';
import PaymentPlanModalSectionGeneral from '@/components/sales/shared/paymentPlan/modal/PaymentPlanModalSections/PaymentPlanModalSectionGeneral.vue';
import PaymentPlanModalSectionBilling from '@/components/sales/shared/paymentPlan/modal/PaymentPlanModalSections/PaymentPlanModalSectionBilling.vue';
import PaymentPlanModalSectionDetails from '@/components/sales/shared/paymentPlan/modal/PaymentPlanModalSections/PaymentPlanModalSectionDetails.vue';
import { extractPayload, toBooleanFlag } from '@/utils/dataNormalization';
import { parsePresentationDate } from '@/utils/datePresentation';
import { formatCurrency } from '@/utils/numberFormat';
import { normalizePaymentPlanRow } from '@/composables/paymentPlans/usePaymentPlanNormalization';
import { ensureOptionPresent, findOptionByRawValue, toPositiveOptionId } from '@/utils/optionValues';
import { PAYMENTS_TEXT } from '@/texts/catalog/payments';
import '@vuepic/vue-datepicker/dist/main.css';

const PLAN_MODAL_TEXT = PAYMENTS_TEXT.planModal;

export default {
  name: 'PaymentPlanModal',
  components: {
    ConfirmActionModal,
    PaymentPlanModalSectionGeneral,
    PaymentPlanModalSectionBilling,
    PaymentPlanModalSectionDetails,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    saleId: {
      type: Number,
      default: 0,
    },
    showCustomerSelector: {
      type: Boolean,
      default: false,
    },
    customerOptions: {
      type: Array,
      default: () => [],
    },
    initialCustomerId: {
      type: Number,
      default: 0,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    salePrice: {
      type: Number,
      default: 0,
    },
    existingPlannedTotal: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      text: PLAN_MODAL_TEXT,
      sections: PAYMENT_PLAN_MODAL_SECTIONS,
      activeSectionKey: 'general',
      optionsLoading: false,
      taxPreviewLoading: false,
      taxPreviewRequestToken: 0,
      taxPreviewDebounceTimer: null,
      taxPreviewDebounceMs: 350,
      isHydratingInitialData: false,
      isOverrunConfirmOpen: false,
      pendingSubmitPayload: null,
      form: createInitialPaymentPlanForm(),
      options: createInitialPaymentPlanModalOptions(),
      firstInstallmentOptions: buildFirstInstallmentOptions(),
      taxPreview: createInitialPaymentPlanTaxPreview(),
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || '').trim().toLowerCase() === 'edit';
    },
    modalTitle() {
      return this.isEditMode ? this.text.modal.title.edit : this.text.modal.title.create;
    },
    modalDescription() {
      return this.isEditMode
        ? this.text.modal.description.edit
        : this.text.modal.description.create;
    },
    submitButtonLabel() {
      return this.isEditMode ? this.text.modal.submit.edit : this.text.modal.submit.create;
    },
    visibleSections() {
      // Facturacion temporalmente oculta: se mostrara nuevamente en la siguiente release.
      return this.sections.filter((section) => section.key !== 'billing');
    },
    initialAmountForEdit() {
      if (!this.isEditMode || !this.initialData || typeof this.initialData !== 'object') {
        return 0;
      }

      const detail = normalizePaymentPlanRow(this.resolveInitialDataSource(this.initialData));
      return Number(detail?.subtotal || detail?.amount || 0) || 0;
    },
    adjustedExistingPlannedTotal() {
      const total = Number(this.existingPlannedTotal || 0) || 0;
      if (!this.isEditMode) {
        return total;
      }

      return Math.max(0, total - this.initialAmountForEdit);
    },
    remainingBalanceBeforeCurrent() {
      const saleTotal = Number(this.salePrice || 0) || 0;
      const assigned = Number(this.adjustedExistingPlannedTotal || 0) || 0;
      return saleTotal - assigned;
    },
    currentAmountNumber() {
      return Number(this.form?.amount || 0) || 0;
    },
    installmentsCount() {
      const frequency = String(this.form?.frequency || '').trim().toLowerCase();
      if (frequency === 'unique') {
        return 1;
      }

      const count = Number(this.form?.paymentsCount || 1) || 1;
      return Math.max(1, count);
    },
    plannedAmountTotal() {
      return this.currentAmountNumber * this.installmentsCount;
    },
    maxAmountPerInstallment() {
      const remaining = Number(this.remainingBalanceBeforeCurrent || 0) || 0;
      if (remaining <= 0) {
        return 0;
      }

      return remaining / this.installmentsCount;
    },
    overrunAmount() {
      return Math.max(0, this.plannedAmountTotal - this.remainingBalanceBeforeCurrent);
    },
    projectedSaleAmount() {
      return (Number(this.salePrice || 0) || 0) + this.overrunAmount;
    },
    overrunConfirmMessage() {
      const currency = String(this.form?.currencyCode || 'MXN').trim().toUpperCase() || 'MXN';
      const saleTotal = formatCurrency(this.salePrice || 0, { currency });
      const totalPlan = formatCurrency(this.plannedAmountTotal, { currency });
      const exceeded = formatCurrency(this.overrunAmount, { currency });
      const projected = formatCurrency(this.projectedSaleAmount, { currency });
      return this.text.overrunConfirm.message({
        saleTotal,
        totalPlan,
        exceeded,
        projected,
      });
    },
    normalizedCustomerOptions() {
      return (Array.isArray(this.customerOptions) ? this.customerOptions : [])
        .filter((option) => String(option?.value || '').trim() !== '');
    },
    resolvedEditPermissions() {
      if (!this.isEditMode) {
        return {
          can_edit_general: true,
          can_edit_billing: true,
          can_edit_amount: true,
          can_edit_payment_date: true,
          can_edit_details: true,
        };
      }

      const detail = normalizePaymentPlanRow(this.resolveInitialDataSource(this.initialData));
      const source = detail?.edit_permissions && typeof detail.edit_permissions === 'object'
        ? detail.edit_permissions
        : {};

      return {
        can_edit_general: toBooleanFlag(source.can_edit_general) || source.can_edit_general === undefined,
        can_edit_billing: toBooleanFlag(source.can_edit_billing) || source.can_edit_billing === undefined,
        can_edit_amount: toBooleanFlag(source.can_edit_amount) || source.can_edit_amount === undefined,
        can_edit_payment_date: toBooleanFlag(source.can_edit_payment_date) || source.can_edit_payment_date === undefined,
        can_edit_details: toBooleanFlag(source.can_edit_details) || source.can_edit_details === undefined,
      };
    },
  },
  validations() {
    return {
      form: {
        concept: {
          required: helpers.withMessage(
            this.text.validation.conceptRequired,
            (value) => String(value || '').trim() !== ''
          ),
        },
        amount: {
          validAmount: helpers.withMessage(
            this.text.validation.amountGreaterThanZero,
            (value) => Number(value || 0) > 0
          ),
        },
        paymentsCount: {
          validRecurringCount: helpers.withMessage(
            this.text.validation.recurringCountGreaterThanZero,
            () => Number(this.form?.paymentsCount || 0) > 0
          ),
        },
        firstInstallmentOption: {
          requiredForMonthly: helpers.withMessage(
            this.text.validation.firstInstallmentRequired,
            (value) => {
              const frequency = String(this.form?.frequency || '').trim().toLowerCase();
              if (frequency !== 'monthly') {
                return true;
              }

              return String(value?.value || '').trim() !== '';
            }
          ),
        },
        periodicityOption: {
          requiredForMultiple: helpers.withMessage(
            this.text.validation.periodicityRequired,
            (value) => {
              const frequency = String(this.form?.frequency || '').trim().toLowerCase();
              if (frequency !== 'multiple') {
                return true;
              }

              return String(value?.value || '').trim() !== '';
            }
          ),
        },
      },
    };
  },
  setup() {
    const v$ = useVuelidate();

    return { v$ };
  },
  watch: {
    show(visible) {
      if (visible) {
        this.handleOpen();
        return;
      }

      this.resetState();
      this.v$.$reset();
    },
    'form.voucherType'(nextValue) {
      const voucher = String(nextValue || '').trim().toLowerCase();
      if (voucher === 'simple_note' && this.activeSectionKey === 'billing') {
        this.activeSectionKey = 'general';
      }
    },
    'form.amount'() {
      this.scheduleTaxPreview();
    },
    'form.taxType'() {
      this.scheduleTaxPreview();
    },
    'form.taxes': {
      deep: true,
      handler() {
        this.scheduleTaxPreview();
      },
    },
    'form.taxGroup'() {
      this.scheduleTaxPreview();
    },
    'form.withoutTaxes'() {
      this.scheduleTaxPreview();
    },
    customerOptions: {
      deep: true,
      handler() {
        if (!this.show || !this.showCustomerSelector) {
          return;
        }

        this.applyInitialCustomerSelection();
      },
    },
    initialCustomerId() {
      if (!this.show || !this.showCustomerSelector) {
        return;
      }

      this.applyInitialCustomerSelection();
    },
  },
  beforeUnmount() {
    this.clearTaxPreviewDebounce();
  },
  methods: {
    resolveInitialDataSource(initialData) {
      const source = initialData && typeof initialData === 'object' ? initialData : {};
      const extracted = extractPayload(source);
      const payload = extracted && Object.keys(extracted).length > 0 ? extracted : source;
      return payload && typeof payload?.data === 'object' ? payload.data : payload;
    },
    resetState() {
      this.activeSectionKey = this.sections[0].key;
      this.form = createInitialPaymentPlanForm();
      this.options = createInitialPaymentPlanModalOptions();
      this.firstInstallmentOptions = buildFirstInstallmentOptions();
      this.taxPreview = createInitialPaymentPlanTaxPreview();
      this.taxPreviewRequestToken = 0;
      this.taxPreviewLoading = false;
      this.optionsLoading = false;
      this.isHydratingInitialData = false;
      this.isOverrunConfirmOpen = false;
      this.pendingSubmitPayload = null;
      this.clearTaxPreviewDebounce();
    },
    async handleOpen() {
      this.resetState();
      await this.loadOptions();

      if (this.initialData && typeof this.initialData === 'object') {
        this.isHydratingInitialData = true;
        this.applyInitialDataToForm(this.initialData);
        this.isHydratingInitialData = false;
      }

      this.applyInitialCustomerSelection();

      this.v$.$reset();

      this.scheduleTaxPreview();
    },
    emitClose() {
      this.$emit('close');
    },
    confirmOverrunSubmit() {
      const payload = this.pendingSubmitPayload;
      this.isOverrunConfirmOpen = false;
      this.pendingSubmitPayload = null;

      if (!payload) {
        return;
      }

      this.$emit('submit', payload);
    },
    cancelOverrunSubmit() {
      this.isOverrunConfirmOpen = false;
      this.pendingSubmitPayload = null;
    },
    clearTaxPreviewDebounce() {
      if (!this.taxPreviewDebounceTimer) {
        return;
      }

      clearTimeout(this.taxPreviewDebounceTimer);
      this.taxPreviewDebounceTimer = null;
    },
    scheduleTaxPreview() {
      if (!this.show) {
        return;
      }

      this.clearTaxPreviewDebounce();
      this.taxPreviewDebounceTimer = setTimeout(() => {
        this.taxPreviewDebounceTimer = null;
        this.fetchTaxPreview();
      }, this.taxPreviewDebounceMs);
    },
    async loadOptions() {
      this.optionsLoading = true;

      try {
        const catalogs = await fetchPaymentPlanModalCatalogs();
        this.options = {
          ...this.options,
          ...catalogs,
        };

        const firstCurrency = this.options.currencies[0]?.value;
        if (firstCurrency && String(this.form.currencyCode || '').trim() === '') {
          this.form.currencyCode = String(firstCurrency).trim().toUpperCase();
        }

        const defaultTax = (Array.isArray(this.options.taxes) ? this.options.taxes : [])
          .find((option) => Number(option?.meta?.default || 0) === 1);
        if (defaultTax && String(this.form?.taxType || '').trim().toLowerCase() === 'individual') {
          this.form.taxes = [defaultTax];
        }
      } finally {
        this.optionsLoading = false;
      }
    },
    resolveCustomerOptionById(rawId, fallbackLabel = '') {
      const customerId = toPositiveOptionId(rawId);
      if (customerId <= 0) {
        return null;
      }

      const existing = findOptionByRawValue(this.normalizedCustomerOptions, customerId);
      if (existing) {
        return existing;
      }

      const label = String(fallbackLabel || '').trim() || this.text.fallback.customerById({ id: customerId });
      return {
        value: customerId,
        label,
      };
    },
    applyInitialCustomerSelection() {
      if (!this.showCustomerSelector) {
        this.form.customer = null;
        return;
      }

      const currentCustomer = this.resolveCustomerOptionById(this.form.customer, this.form.customer?.label);
      if (currentCustomer) {
        this.form.customer = currentCustomer;
        return;
      }

      const preferred = this.resolveCustomerOptionById(this.initialCustomerId);
      if (preferred) {
        this.form.customer = preferred;
        return;
      }

      this.form.customer = this.normalizedCustomerOptions[0] || null;
    },
    applyTaxSelectionFromDetail(data = {}) {
      const detail = normalizePaymentPlanRow(data);
      const taxId = toPositiveOptionId(detail.tax_id);
      const taxGroupId = toPositiveOptionId(detail.tax_group_id);

      if (taxId > 0) {
        this.form.withoutTaxes = false;
        this.form.taxType = 'individual';
        this.form.taxGroup = null;

        const selectedTax = findOptionByRawValue(this.options.taxes, taxId)
          || { value: taxId, label: this.text.fallback.taxById({ id: taxId }) };
        const taxResolution = ensureOptionPresent(this.options, 'taxes', selectedTax);
        this.options = taxResolution.options;
        const safeTax = taxResolution.option;
        this.form.taxes = safeTax ? [safeTax] : [];
        return;
      }

      if (taxGroupId > 0) {
        this.form.withoutTaxes = false;
        this.form.taxType = 'group';
        this.form.taxes = [];
        const selectedGroup = findOptionByRawValue(this.options.taxGroups, taxGroupId)
          || { value: taxGroupId, label: this.text.fallback.taxGroupById({ id: taxGroupId }) };
        const groupResolution = ensureOptionPresent(this.options, 'taxGroups', selectedGroup);
        this.options = groupResolution.options;
        this.form.taxGroup = groupResolution.option;
        return;
      }

      this.form.withoutTaxes = true;
      this.form.taxType = 'individual';
      this.form.taxes = [];
      this.form.taxGroup = null;
    },
    applyInitialDataToForm(initialData) {
      const data = normalizePaymentPlanRow(this.resolveInitialDataSource(initialData));

      const voucherTypeFromSpecial = Number(data.special || 0) === 1 ? 'simple_note' : 'cfdi';
      const voucherType = String(data.voucher_type || data.voucherType || voucherTypeFromSpecial).trim().toLowerCase();
      if (voucherType !== '') {
        this.form.voucherType = voucherType;
      }

      const currencyCode = String(data.currency || data.currencyCode || '').trim().toUpperCase();
      if (currencyCode !== '') {
        this.form.currencyCode = currencyCode;
      }

        this.form.exchangeRate = Number(data.exchange_rate || data.type_change || this.form.exchangeRate || 1) || 1;
        this.form.amount = Number(data.subtotal || data.amount || 0) || 0;

        const invoiced = toBooleanFlag(data.invoiced);
        const invoiceId = toPositiveOptionId(data.invoice_id);
        const invoiceReference = String(data.invoice_reference || data.invoice || '').trim();

      if (!invoiced && invoiceId <= 0 && invoiceReference === '') {
        this.form.billingStatus = 'unbilled';
        this.form.orderlyInvoice = null;
        this.form.orderlyInvoiceId = null;
        this.form.externalInvoiceReference = '';
      } else if (invoiceId > 0) {
        this.form.billingStatus = 'orderly';
        const invoiceOption = {
          value: invoiceId,
          label: invoiceReference || this.text.fallback.invoiceById({ id: invoiceId }),
        };
        const invoiceResolution = ensureOptionPresent(this.options, 'invoices', invoiceOption);
        this.options = invoiceResolution.options;
        this.form.orderlyInvoice = invoiceResolution.option;
        this.form.orderlyInvoiceId = invoiceId;
        this.form.externalInvoiceReference = '';
      } else {
        this.form.billingStatus = 'external';
        this.form.orderlyInvoice = null;
        this.form.orderlyInvoiceId = null;
        this.form.externalInvoiceReference = String(invoiceReference || '').trim();
      }

      this.form.area = findOptionByRawValue(
        this.options.areas,
        [data.area, data.area_id, data.supplier?.area_id]
      );

      this.applyTaxSelectionFromDetail(data);

      const frequency = String(data.frequency || '').trim().toLowerCase();
      if (frequency === 'monthly' || frequency === 'multiple' || frequency === 'unique') {
        this.form.frequency = frequency;
      }

      this.form.paymentsCount = Math.max(
        1,
        Number(data.payments_count || data.expenses_count || 1) || 1
      );

      const firstInstallment = String(data.first_installment || '').trim();
      if (firstInstallment !== '') {
        this.form.firstInstallmentOption = findOptionByRawValue(this.firstInstallmentOptions, firstInstallment);
      }

      const periodicityCode = String(data.periodicity_code || '').trim().toLowerCase();
      if (periodicityCode !== '') {
        this.form.periodicityOption = findOptionByRawValue(this.options.periodicities, periodicityCode);
      }

      this.form.folio = String(data.folio || '').trim();
      this.form.concept = String(data.concept || '').trim();
      this.form.comments = String(data.comments || '').trim();

      const expenseDate = parsePresentationDate(data.date);
      if (expenseDate) {
        this.form.expenseDate = expenseDate;
      }

      const paymentDate = parsePresentationDate(data.payment_date);
      if (paymentDate) {
        this.form.paymentDate = paymentDate;
      }

      const cfdiDate = parsePresentationDate(data.cfdi_date || data.invoice_date);
      if (cfdiDate) {
        this.form.cfdiDate = cfdiDate;
      }

      const customerId = toPositiveOptionId(data.company_id);
      if (customerId > 0) {
        this.form.customer = this.resolveCustomerOptionById(
          customerId,
          data.company_name
        );
      }

      // Re-apply taxes after sync watchers settle to avoid losing selected options in edit mode.
      this.$nextTick(() => {
        this.applyTaxSelectionFromDetail(data);
      });
    },
    async fetchTaxPreview() {
      const requestToken = this.taxPreviewRequestToken + 1;
      this.taxPreviewRequestToken = requestToken;
      this.taxPreviewLoading = true;

      try {
        const response = await taxesService.preview(
          buildPaymentPlanTaxPreviewPayload(this.form),
          { skipLoading: true }
        );
        if (requestToken !== this.taxPreviewRequestToken) {
          return;
        }

        const payload = extractPayload(response);
        this.taxPreview = {
          ...createInitialPaymentPlanTaxPreview(),
          ...(payload && typeof payload === 'object' ? payload : {}),
        };
      } catch (error) {
        if (requestToken === this.taxPreviewRequestToken) {
          this.taxPreview = createInitialPaymentPlanTaxPreview();
        }
      } finally {
        if (requestToken === this.taxPreviewRequestToken) {
          this.taxPreviewLoading = false;
        }
      }
    },
    submitForm() {
      if (this.showCustomerSelector && toPositiveOptionId(this.form.customer) <= 0) {
        this.activeSectionKey = 'general';
        this.$toast?.error(this.text.validation.customerRequired);
        return;
      }

      this.v$.$touch();

      if (this.v$.$invalid) {
        this.activeSectionKey = 'general';
        return;
      }

      const payload = buildPaymentPlanPayload(this.form, this.taxPreview, { saleId: this.saleId });

      this.$emit('submit', payload);
    },
  },
};
</script>
